import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTelegram } from '../telegram';

const STORAGE_KEY = 'rentstation_invoice_uid';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rentstation.ru';

export interface CartItem {
    id: number;
    item_id: number;
    item_title: string;
    item_image: string;
    start: string;
    end: string;
    days: number;
    price_per_day: number;
    price: number;
    deposit: number;
}

export interface CartInvoice {
    uid: string;
    id: number;
    bookings: CartItem[];
    price: number;
    deposit: number;
    total: number;
    person_firstname: string | null;
    person_lastname: string | null;
    person_email: string | null;
    person_phone: string | null;
    is_payed: boolean;
}

interface CartContextValue {
    items: CartItem[];
    invoice: CartInvoice | null;
    invoiceUid: string | null;
    loading: boolean;
    addItem: (itemId: number, start: string, end: string) => Promise<void>;
    removeItem: (bookingId: number) => Promise<void>;
    refresh: () => Promise<void>;
    clearCart: () => void;
    itemCount: number;
}

const CartContext = createContext<CartContextValue | null>(null);

export function useCart(): CartContextValue {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}

interface CartProviderProps {
    children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
    const { initData } = useTelegram();
    const [invoice, setInvoice] = useState<CartInvoice | null>(null);
    const [invoiceUid, setInvoiceUid] = useState<string | null>(() => {
        return localStorage.getItem(STORAGE_KEY);
    });
    const [loading, setLoading] = useState(false);

    const getAuthHeaders = useCallback((): Record<string, string> => {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (initData) {
            headers['Authorization'] = `tma ${initData}`;
        }
        return headers;
    }, [initData]);

    // Load cart from API on mount if we have a stored uid
    const refresh = useCallback(async () => {
        const uid = localStorage.getItem(STORAGE_KEY);
        if (!uid) {
            setInvoice(null);
            setInvoiceUid(null);
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/cart?uid=${uid}`, {
                headers: getAuthHeaders(),
            });

            if (!res.ok) {
                // Cart not found or invalid - clear local storage
                localStorage.removeItem(STORAGE_KEY);
                setInvoice(null);
                setInvoiceUid(null);
                return;
            }

            const data: CartInvoice = await res.json();
            setInvoice(data);
            setInvoiceUid(data.uid);
        } catch (err) {
            console.error('Failed to load cart:', err);
            // On error, clear the stored uid
            localStorage.removeItem(STORAGE_KEY);
            setInvoice(null);
            setInvoiceUid(null);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    // Load cart on mount
    useEffect(() => {
        refresh();
    }, [refresh]);

    const addItem = useCallback(async (itemId: number, start: string, end: string) => {
        setLoading(true);
        try {
            const currentUid = localStorage.getItem(STORAGE_KEY);
            const res = await fetch(`${API_BASE_URL}/api/cart/items`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    invoice_uid: currentUid || undefined,
                    item_id: itemId,
                    start_date: start,
                    end_date: end,
                }),
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to add item to cart');
            }

            const data: CartInvoice = await res.json();

            // Store the uid
            localStorage.setItem(STORAGE_KEY, data.uid);
            setInvoice(data);
            setInvoiceUid(data.uid);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    const removeItem = useCallback(async (bookingId: number) => {
        const uid = localStorage.getItem(STORAGE_KEY);
        if (!uid) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/cart/items/${bookingId}?uid=${uid}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to remove item from cart');
            }

            const data: CartInvoice = await res.json();
            setInvoice(data);

            // If cart is now empty, clear storage
            if (data.bookings.length === 0) {
                localStorage.removeItem(STORAGE_KEY);
                setInvoice(null);
                setInvoiceUid(null);
            }
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    const clearCart = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setInvoice(null);
        setInvoiceUid(null);
    }, []);

    const value: CartContextValue = {
        items: invoice?.bookings || [],
        invoice,
        invoiceUid,
        loading,
        addItem,
        removeItem,
        refresh,
        clearCart,
        itemCount: invoice?.bookings?.length || 0,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}
